import React, { useContext, useEffect, useState, useCallback } from "react";
import ReactDOM from 'react-dom';
import { useDropzone } from 'react-dropzone';
import { makeStyles } from "@material-ui/core/styles";
import { AppContext, DispatchContext } from "../AppContext";
import * as actions from "../actions/actions";
import Service from "../services/service";
import { createUUID, formatFileSize, getCookie, showCaptcha, config, resolveNotificationMessage } from '../helpers/constants';
import { LinearProgress, Paper, Divider, Box, Typography, Button, TextField } from "@material-ui/core";
import FullScreenPage from "./FullScreenPage";
import { useParams } from "react-router-dom";
import LoadingScreen from './LoadingScreen';
import IconLabel from './IconLabel';
import FileUploadCards from "./FileUploadCards";
import { ReactComponent as CheckedIcon } from '../assets/icons/checked.svg';
import { ReactComponent as UploadingIcon } from '../assets/icons/uploading.svg';
import { ReactComponent as FileErrorIcon } from '../assets/icons/fileError.svg';


const useStyles = makeStyles((theme) => ({
  paper: {
    width: 800,
    height: 500,
    position: "relative",
    boxShadow: "0px 0px 5px 5px rgba(0, 0, 0, 0.25)",
    "&:hover": {
      boxShadow: "0px 0px 7px 7px rgba(0, 0, 0, 0.4)",
      transition: "box-shadow 0.3s ease-in-out",
    }
  },
  logo: {
    width: "max-content",
    alignSelf: "flex-end"
  },
  drag_drop_container: {
    overflowX: "hidden",
    overflowY: "auto",
    flexDirection: "column",
    display: "flex",
    height: 260,
    padding: 8,
    cursor: "pointer",
    '&:focus': {
      outline: 'none'
    }
  }
}));

let uploadedFiles = [];
let xhrs = [];
let uploadStatusTimer = 0;


export default function UploadForm(props) {
  const dispatch = useContext(DispatchContext);
  const state = useContext(AppContext);
  const { id } = useParams();
  const classes = useStyles();
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [projectCodeValid, setProjectCodeValid] = useState(false);
  const [submissionState, setSubmissionState] = useState("upload"); // one of upload, submitted, skipped
  const s = state.strings;

  useEffect(() => {
    return () => {
      onCancelAllFilesUpload()
    };
  }, []);


  useEffect(() => {
    if (!state.user.loggedIn) {
      showCaptcha();
    }
    getUploadForm(id);
  }, []);

  function getUploadForm(id) {
    setLoading(true);
    Service.getUploadForm(id).then(res => {
      setLoading(false);
      setProjectCodeValid(res.success);
      if (res.success) {
        setFormData(res.data)
      } else {
        dispatch(actions.openToast({ type: "error", message: resolveNotificationMessage(res.errors[0], s.notificationMessage) }));
      }
    });
  }

  const onSubmitButtonClicked = () => {
    captchaAction("submitFile", submitFile);
  }

  const submitFile = () => {
    setLoading(true);
    Service.AddTextsToLibrary({ uploadids: uploadedFiles, projectcode: id }).then(res => {
      onCancelAllFilesUpload();
      setLoading(false);
      if (res.success) {
        if (res.message.includes("textsSkipped")) {
          dispatch(actions.openToast({ type: "warning", message: s.newCheck.textSkipped }));
          setSubmissionState("skipped");
        } else {
          dispatch(actions.openToast({ message: s.newCheck.textSubmitted }));
          setSubmissionState("submitted");
        }
      } else {
        dispatch(actions.openToast({ type: "error", message: resolveNotificationMessage(res.errors[0], s.notificationMessage) }));
      }
    });
  }

  const onNewButtonClicked = () => {
    onCancelAllFilesUpload();
    setLoading(false);
    setSubmissionState("upload");
  }


  const onDrop = useCallback(acceptedFiles => {
    let maxAllowedCount = config.maxNumberFileUploadForm - uploadedFiles.length;
    if (acceptedFiles.length > maxAllowedCount) {
      let skippedCount = acceptedFiles.length - maxAllowedCount;
      dispatch(actions.openToast({ type: "warning", message: s.newCheck.filesTruncated.format(config.maxNumberFileUploadForm, skippedCount), status: true }));
      acceptedFiles = acceptedFiles.slice(0, maxAllowedCount);
    }

    acceptedFiles.map(file => {
      const uuid = createUUID();
      file.id = uuid;
    })

    dispatch(actions.setUploadedFiles(acceptedFiles));

    acceptedFiles.map((file, i) => {
      const uuid = file.id;
      const formData = new FormData();
      formData.append('file', file);
      formData.append('uploadid', uuid);
      formData.append('projectcode', id);
      formData.append('sid', getCookie(config.sessionCookieName));
      if (document.getElementById(uuid)) {
        ReactDOM.render(<LinearProgress variant="determinate" value={0} />, document.getElementById(uuid))
      }
      let xhr = new XMLHttpRequest();
      xhrs.push(xhr);

      xhr.upload.onprogress = (e) => {
        const pct = +((e.loaded / e.total) * 100).toFixed(2);
        if (document.getElementById(uuid + '_status')) {
          ReactDOM.render(`${formatFileSize(e.loaded)} / ${formatFileSize(e.total)} (${pct})%`, document.getElementById(uuid + '_status'));
        }
        if (document.getElementById(uuid)) {
          ReactDOM.render(pct !== 100 ? <LinearProgress variant="determinate" value={pct} /> : <LinearProgress value={pct} />, document.getElementById(uuid))
        }
      };
      uploadedFiles.push(uuid)
      xhr.onreadystatechange = () => {

        if (xhr.readyState !== 4) return;   // 4: DONE
        if (xhr.status !== 200) {
          return;
        }
        const response = JSON.parse(xhr.response)
        if (response && !response.success) {
          onUploadedDeleteFile(uuid);
          dispatch(actions.openToast({ type: "error", message: response.errors, status: true }))
        }
        /*handle success*/
      };

      xhr.open("post", config.backendUrl + "api/UploadFile");
      xhr.send(formData);
    });

    getUploadedFilesStatus(uploadedFiles)

  }, []);

  const onUploadedDeleteFile = (id) => {
    // todo: abort xhr for this file
    uploadedFiles = uploadedFiles.filter(file => file !== id);
    if (uploadedFiles.length > 0) {
      getUploadedFilesStatus(uploadedFiles)
    }
    else {
      onCancelAllFilesUpload();
    }
    dispatch(actions.removeUploadedFile(id));
  }

  const getUploadedFilesStatus = (uploadids) => {

    clearInterval(uploadStatusTimer);

    uploadStatusTimer = setInterval(() => {
      Service.getUploadedFilesStatus(uploadids).then(res => {

        res.data.Files && res.data.Files.filter(f => f.Status == "Error").map(f => {
          onUploadedDeleteFile(f.UploadId);
          dispatch(actions.openToast({ type: "error", message: s.newCheck.uploadError, status: true }))
        });

        if (res.data.PendingCount === 0 && res.total !== 0 && uploadids.length === res.total) {
          clearInterval(uploadStatusTimer);
        }
        dispatch(actions.setUploadedFilesStatus(res.data))
        // check if there are files in status=Error and delete them from the list

      })
    }, 2500);
  }

  const onCancelAllFilesUpload = () => {
    xhrs.map((x) => x.abort());
    xhrs = [];
    uploadedFiles = [];
    clearInterval(uploadStatusTimer);
    dispatch(actions.cancelFilesUpload());
    dispatch(actions.setUploadedFilesStatus({ Files: [] }))
  }

  const captchaAction = (action, fn) => {
    const captchas = document.getElementsByClassName("grecaptcha-badge");
    if (captchas.length > 0 && captchas[0].style.visibility != "hidden") {
      window.grecaptcha.ready(() => {
        window.grecaptcha.execute(config.captchaKey, { action: action }).then(function (token) {
          Service.verifyReCaptcha(token).then((verifyRes) => {
            if (verifyRes.success) {
              fn();
            } else {
              setLoading(false);
              dispatch(actions.openToast({ type: "error", message: resolveNotificationMessage(verifyRes.errors[0], s.notificationMessage) }));
            }
          });
        });
      });
    } else {
      fn();
    }
  }

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });
  const uploadedOkCount = state.uploadedFilesStatus.filter(file => file.Status == "Ok").length;

  return (
    <FullScreenPage>
      <Paper className={classes.paper}>
        <LoadingScreen status={loading} />
        {!projectCodeValid && !loading && <IconLabel centered type="warning" label={s.notificationMessage.projectCodeNotValid} />}
        {projectCodeValid && !loading &&
          <div style={{ padding: 16, display: "flex", flexDirection: "column" }}>
            <img className={classes.logo} src={config.backendUrl + formData.logo} alt="logo" />
            <Typography paragraph variant="h6" color="primary">{formData.header}</Typography>
            <Typography variant="body2" color="secondary">{formData.description}</Typography>
            <Divider style={{ marginTop: 8, marginBottom: 16 }} />

            {submissionState == "submitted" ?
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", width: "100%", padding: 40 }}>
                <CheckedIcon style={{ fill: '#009900' }} height={100} width={100} />
                <Box mt={2} />
                <Typography paragraph variant="h6">{s.uploadFormular.thankYou}</Typography>
                <Typography align="center" color="secondary">{s.uploadFormular.textSubmitted}</Typography>
              </div>
              :
              (submissionState == "skipped") ?
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", width: "100%", padding: 40 }}>
                  <FileErrorIcon style={{ fill: '#FF9800' }} height={100} width={100} />
                  <Box mt={2} />
                  <Typography paragraph variant="h6">{s.uploadFormular.alreadyExisting}</Typography>
                  <Typography align="center" color="secondary">{s.uploadFormular.textSkipped}</Typography>
                </div>
                :
                <div className={classes.drag_drop_container} {...getRootProps()}>
                  <input {...getInputProps()} />
                  {state.uploadedFiles.length > 0 ?
                    <>
                      <Box mt={6} />
                      <FileUploadCards singleFile acceptedFiles={state.uploadedFiles} fileStatus={state.uploadedFilesStatus} onUploadedDeleteFile={onUploadedDeleteFile} isDragActive={isDragActive} />
                    </> :
                    isDragActive ?
                      <div style={{ borderRadius: 5, border: "2px solid #0D4A81", backgroundColor: "#eeeeee", width: "100%", height: "100%", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
                        <UploadingIcon fill="#1982DD" height={100} width={100} style={{ marginBottom: 16 }} />
                        <Typography align="center">{s.newCheck.dropHere}</Typography>
                      </div> :
                      <div style={{ borderRadius: 5, border: "2px dotted #0D4A81", width: "100%", height: "100%", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
                        <UploadingIcon fill="#0D4A81" height={100} width={100} style={{ marginBottom: 16 }} />
                        <Typography paragraph align="center">{s.dragYourFileHere}</Typography>
                        <Typography variant="body2" color="secondary" align="center">{s.newCheck.parallelUpload.format(config.maxNumberFileUploadForm)}</Typography>
                        <Typography variant="body2" color="secondary" align="center">{s.newCheck.supportFileFormats}</Typography>
                        <Typography variant="body2" color="secondary" align="center">{s.newCheck.maxFileSize}</Typography>
                      </div>
                  }
                </div>
            }
            <Divider style={{ marginTop: 8, marginBottom: 24 }} />

            {submissionState == "upload" ?
              <Button onClick={onSubmitButtonClicked} disabled={!uploadedOkCount} style={{ alignSelf: "center", width: 300 }} variant="contained" color="primary">{s.newCheck.submitFile}</Button>
              :
              <Button onClick={onNewButtonClicked} style={{ alignSelf: "center", width: 300 }} variant="contained" color="primary">{s.uploadFormular.uploadOtherFile}</Button>
            }

          </div>
        }

      </Paper>
    </FullScreenPage>);
}
